import { useState, useEffect, useRef } from 'react';

/**
 * Commit the measured height only after resizing settles. Continuous window /
 * pane resizing fires the ResizeObserver every frame; committing each one
 * re-renders every consumer (and their subtrees) 60×/sec. The first
 * measurement is committed immediately (correct first paint); only subsequent
 * resize-driven updates are debounced.
 */
const RESIZE_SETTLE_MS = 100;

/**
 * Measures the actual rendered height of a container element using ResizeObserver.
 * Used when a child component (e.g., PdfViewer) requires an explicit pixel height
 * but the available space is determined by CSS flex layout.
 *
 * Usage:
 *   const { ref, height } = useContainerHeight();
 *   <div ref={ref} className="flex-1 min-h-0">
 *     <PdfViewer heightCard={`${height}px`} />
 *   </div>
 *
 * The parent must use `display: flex; flex-direction: column; height: 100vh`
 * and the measured container must use `flex: 1; min-height: 0` so the browser
 * calculates the remaining space before ResizeObserver reports it.
 *
 * The measured container must never be sized by its own content — writing the
 * measured value back into a descendant of a content-sized container closes a
 * feedback loop. The rounding + equality guard + debounce below damp the
 * symptoms; they do not remove a genuine loop, so the layout must be correct.
 *
 * @returns {{ ref: React.RefObject<HTMLElement>, height: number }} height in px
 *   (rounded, 0 until the first measurement)
 */
const useContainerHeight = () => {
  const ref = useRef(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const commit = (measured) => {
      const nextHeight = Math.round(measured);
      setHeight((prev) => (prev !== nextHeight ? nextHeight : prev));
    };

    let isFirstMeasurement = true;
    let timeoutId = null;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      const measured = entry.contentRect.height;

      // First callback → commit now so the initial layout is correct without
      // waiting a debounce window. Subsequent callbacks (resize) are debounced.
      if (isFirstMeasurement) {
        isFirstMeasurement = false;
        commit(measured);
        return;
      }

      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => commit(measured), RESIZE_SETTLE_MS);
    });

    observer.observe(el);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, []);

  return { ref, height };
};

export default useContainerHeight;
